/**
 * The status chip beside the Delivery Details title.
 *
 * CR-4704 gave it the GRN's own workflow state (the supplier acknowledging or
 * disputing the delivery). EXPEDITING-3 (Aaron, 30 Sep 2026): a delivery whose
 * OTIF result was decided in OTIF review shows that decision in place of
 * "Pending Review", in the decision's own colours (CR-7418: teal Overridden,
 * violet Acknowledged) -- an acknowledgement only while its Early/Late verdict
 * still stands, since the supplier may still appeal it.
 */
export interface DeliveryHeaderFields {
  workflow_status?: string | null;
  otif_overridden?: boolean;
  otif_acknowledged?: boolean;
  on_time_status?: string | null;
}

export interface DeliveryHeaderStatus {
  label: string;
  className: string;
  tone: 'done' | 'warning' | 'pending';
}

/**
 * @param grn - The delivery's workflow state and OTIF decision.
 * @returns The chip to show, or null for none.
 */
export function deliveryHeaderStatus(grn: DeliveryHeaderFields): DeliveryHeaderStatus | null {
  if (grn.workflow_status === 'acknowledged') {
    return { label: 'Acknowledged', className: 'bg-green-100 text-green-800', tone: 'done' };
  }
  if (grn.workflow_status === 'disputed') {
    return { label: 'Disputed', className: 'bg-red-100 text-red-800', tone: 'warning' };
  }
  if (grn.workflow_status === 'pending_review') {
    if (grn.otif_overridden) return { label: 'OTIF Overridden', className: 'bg-teal-100 text-teal-800', tone: 'done' };
    // Only while the verdict it accepted still stands: once an accepted appeal
    // makes the delivery on time, that is what shows (Aaron, 30 Sep 2026).
    const failStands = grn.on_time_status === 'early' || grn.on_time_status === 'late';
    if (grn.otif_acknowledged && failStands) {
      return { label: 'OTIF Acknowledged', className: 'bg-violet-100 text-violet-800', tone: 'done' };
    }
    return { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-800', tone: 'pending' };
  }
  return null;
}
